import type { FileSegment, ImageParser, ParseResult } from './Parser';
import { getGifDescription } from './GifDescriptions';
// GIF doesn't typically store Exif in the standard way (APP1).
// XMP can be embedded in Application Extension but basic GIF lacks it.

export class GifParser implements ImageParser {
  parse(data: Uint8Array): ParseResult {
    const segments: FileSegment[] = [];
    const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
    let i = 0;

    // Header (6 bytes): GIF89a or GIF87a
    if (data.length < 6) return { segments: [], format: 'Unknown' };

    segments.push({
      index: segments.length,
      type: 'HDR',
      name: 'Header',
      start: 0,
      payloadStart: 0, // Text
      end: 6,
      size: 6
    });
    i = 6;

    // Logical Screen Descriptor (7 bytes)
    if (i + 7 > data.length) return { segments, format: 'GIF' }; // truncated

    // Extract Dimensions early
    const metadata: Record<string, string> = {
      'Dimensions': `${view.getUint16(6, true)}x${view.getUint16(8, true)}`
    };

    // Packed field at offset 4 (relative to current i) tells about Global Color Table
    const packed = view.getUint8(i + 4);
    const gctFlag = (packed & 0x80) !== 0;
    const gctSizeExp = packed & 0x07;
    const gctSize = 3 * (1 << (gctSizeExp + 1));

    segments.push({
      index: segments.length,
      type: 'LSD',
      name: 'Logical Screen Descriptor',
      start: i,
      payloadStart: i,
      end: i + 7,
      size: 7
    });
    i += 7;

    // Global Color Table
    if (gctFlag) {
      if (i + gctSize > data.length) return { segments, format: 'GIF', metadata };
      segments.push({
        index: segments.length,
        type: 'GCT',
        name: 'Global Color Table',
        start: i,
        payloadStart: i,
        end: i + gctSize,
        size: gctSize
      });
      i += gctSize;
    }

    // Parse Blocks
    while (i < data.length) {
      const separator = data[i];

      if (separator === 0x3B) { // Trailer
        segments.push({
          index: segments.length,
          type: 'TRL',
          name: 'Trailer',
          start: i,
          payloadStart: i,
          end: i + 1,
          size: 1
        });
        break;
      }

      if (separator === 0x21) { // Extension
        // 0x21, Label, Block Size
        if (i + 2 > data.length) break;
        const label = data[i + 1];
        let name = 'Extension';
        if (label === 0xF9) name = 'Graphic Control Extension';
        if (label === 0xFE) name = 'Comment Extension';
        if (label === 0x01) name = 'Plain Text Extension';
        if (label === 0xFF) name = 'Application Extension';

        let currentBlock = i + 2;
        // Blocks are sub-blocks until 0x00
        while (currentBlock < data.length) {
          const blockSize = data[currentBlock];
          currentBlock++;
          if (blockSize === 0) break;
          currentBlock += blockSize;
        }

        const end = currentBlock;

        segments.push({
          index: segments.length,
          type: `EXT:${label.toString(16)}`,
          name: name,
          start: i,
          payloadStart: i + 2,
          end: end,
          size: end - i
        });
        i = end;

      } else if (separator === 0x2C) { // Image Descriptor
        // 0x2C, Left(2), Top(2), W(2), H(2), Packed(1) = 10 bytes
        if (i + 10 > data.length) break;

        const imgPacked = data[i + 9];
        const lctFlag = (imgPacked & 0x80) !== 0;
        const lctSizeExp = imgPacked & 0x07;
        const lctSize = 3 * (1 << (lctSizeExp + 1));

        let end = i + 10;
        if (lctFlag) {
          end += lctSize;
        }

        // Following LCT is Image Data (LZW)
        // Sub-blocks until 0 check.
        let dataStart = end;
        if (dataStart < data.length) {
          // LZW Minimum Code Size (1 byte)
          dataStart++;

          let current = dataStart;
          while (current < data.length) {
            const bSize = data[current];
            current++;
            if (bSize === 0) break;
            current += bSize;
          }
          end = current;
        }

        segments.push({
          index: segments.length,
          type: 'IMG',
          name: 'Image Data', // Includes Descriptor + LCT + Data
          start: i,
          payloadStart: i + 10,
          end: end,
          size: end - i
        });
        i = end;
      } else {
        // Unknown trailing data or sync lost
        i++;
      }
    }

    return {
      segments,
      format: 'GIF',
      metadata
    };
  }

  getDescription(name: string): string {
    return getGifDescription(name);
  }
}
